/**
 * Progress — the per-bureau rows after the 202 (mission 7 WP10 T054; FR-011, FR-028, NFR-008; TX.5, TX.6,
 * AV.7; sheet 31 callouts 11 and 15; squad U8, U10; contracts/ui-api.md "After the 202").
 *
 * One row per attempt the 202 named, in a polite live region. Each row reads the polled per-run entries by
 * `transmission_id`: `progressSending` while pending (or not listed yet), `progressQueued` once 10 s have
 * passed since the 202 was received on this client and it is still pending (U10: the server's
 * `attempted_at` never starts it), then `TransmissionResultBadge` and the detail sentence once resolved.
 * The page's per-run query does the polling and stops it (NFR-008); this component only reads.
 *
 * Focus moves to the region's heading when the rows appear and again when nothing is pending, and then
 * `viewTransmissionLog` links to the run's log (U8). mission 7 WP14: J3, the heading is its own register key,
 * `progressHeading`, worded as the log's name was.
 *
 * Mission 7 review follow-up 6 (UI-1; FR-011, NFR-008): when a poll failed (`interrupted`), polling has stopped,
 * so the rows are the last status read. A warning band says so (`transmitPollInterrupted`) and links to the run's
 * log, and a pending row keeps `progressSending`: never `progressQueued`, which would blame the delivery service.
 *
 * Mission 9 WP14 T091 (31/11; FR-016's UI half; NF.9, TX.6; research §1.23): a resolved row adds
 * `transmissionEmailNote`, since each resolution writes one `transmission_result` email.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { TransmissionResultBadge } from '@/components/trellis/TransmissionResultBadge'
import { bureauName } from '@/lib/api/bureaus'
import type { TransmissionDetail, TransmissionEntry, TransmitAttempt } from '@/lib/api/transmissions'
import { copyText, type TextKey } from '@/lib/provisional-copy'

/** AV.7's notice: how long after the 202 a pending attempt reads as queued (U10). */
export const QUEUED_AFTER_MS = 10_000

const DETAIL: Record<TransmissionDetail, TextKey> = {
  'auth-rejected': 'detail.authRejected',
  'host-timeout': 'detail.hostTimeout',
  interrupted: 'detail.interrupted',
  'mgts-unavailable': 'detail.mgtsUnavailable',
  'credential-changed': 'detail.credentialChanged',
  'file-unavailable': 'detail.fileUnavailable',
  'bureau-disabled': 'detail.bureauDisabled',
  'host-refused': 'detail.hostRefused',
  'not-attempted': 'detail.notAttempted',
}

export interface ProgressProps {
  runId: string
  /** The 202's attempts. */
  attempts: readonly TransmitAttempt[]
  /** `Date.now()` when the 202 was received (U10). */
  acceptedAt: number
  /** The polled per-run entries; undefined before the first read. */
  entries: readonly TransmissionEntry[] | undefined
  /** A poll failed, so polling stopped and `entries` is the last read (review follow-up 6). */
  interrupted?: boolean
}

function Row({ attempt, entry, queued }: { attempt: TransmitAttempt; entry: TransmissionEntry | undefined; queued: boolean }) {
  const pending = entry === undefined || entry.result === 'pending'
  return (
    <li data-progress-row={attempt.bureau} className="flex flex-col gap-1 border-b border-navy-200 py-3 last:border-0">
      <div className="flex items-center gap-3">
        <span className="min-w-28 text-body font-medium text-navy-950">{bureauName(attempt.bureau)}</span>
        {pending ? (
          <span className="text-body text-navy-700">{copyText(queued ? 'progressQueued' : 'progressSending')}</span>
        ) : (
          <TransmissionResultBadge result={entry.result} />
        )}
      </div>
      {!pending && entry.detail ? <p className="text-body-sm text-navy-700">{copyText(DETAIL[entry.detail])}</p> : null}
      {/* Mission 9 WP14 T091 (31/11; FR-016; NF.9): the resolution also writes the transmission_result email. */}
      {!pending ? <p className="text-caption text-navy-600">{copyText('transmissionEmailNote')}</p> : null}
    </li>
  )
}

export function Progress({ runId, attempts, acceptedAt, entries, interrupted = false }: ProgressProps) {
  const heading = useRef<HTMLHeadingElement>(null)
  const [queuedDue, setQueuedDue] = useState(() => Date.now() - acceptedAt >= QUEUED_AFTER_MS)

  useEffect(() => {
    if (queuedDue) return undefined
    const timer = setTimeout(() => setQueuedDue(true), Math.max(0, acceptedAt + QUEUED_AFTER_MS - Date.now()))
    return () => clearTimeout(timer)
  }, [acceptedAt, queuedDue])

  const byId = new Map((entries ?? []).map((e) => [e.transmission_id, e]))
  const done = attempts.every((a) => {
    const e = byId.get(a.transmission_id)
    return e !== undefined && e.result !== 'pending'
  })

  useEffect(() => {
    heading.current?.focus()
  }, [done])

  const logLink = (
    <Link
      to={`/app/runs/${encodeURIComponent(runId)}/transmissions`}
      className="text-body text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]"
    >
      {copyText('viewTransmissionLog')}
    </Link>
  )

  return (
    <section className="flex flex-col gap-2 rounded-lg border border-navy-200 bg-white px-4 py-3">
      <h2 ref={heading} tabIndex={-1} className="text-h3 text-navy-950 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]">
        {/* mission 7 WP14: J3, its own key (was crumb.transmissions). */}
        {copyText('progressHeading')}
      </h2>
      {interrupted ? (
        <Band kind="warning" action={logLink}>
          <p>{copyText('transmitPollInterrupted')}</p>
        </Band>
      ) : null}
      <ul aria-live="polite" className="flex flex-col">
        {attempts.map((a) => (
          <Row key={a.transmission_id} attempt={a} entry={byId.get(a.transmission_id)} queued={queuedDue && !interrupted} />
        ))}
      </ul>
      {done && !interrupted ? <p>{logLink}</p> : null}
    </section>
  )
}
